import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaTrash, FaEdit } from "react-icons/fa";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase"; // Import your Firebase config

const ToDoList = () => {
  const [tasks, setTasks] = useState([]);
  const [newTaskName, setNewTaskName] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState("");
  const [editingPriority, setEditingPriority] = useState("");
  const [userId, setUserId] = useState(null); // Track the user ID

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid); // Set the current user's UID
      } else {
        setUserId(null); // Reset if user is signed out
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchTasks = async () => {
      const tasksRef = collection(db, "tasks");
      const q = query(tasksRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);
      const tasksData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTasks(tasksData);
    };

    fetchTasks();
  }, [userId]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) return;

    const newTask = {
      name: newTaskName,
      priority,
      completed: false,
      userId,
    };

    const docRef = doc(collection(db, "tasks"));
    await setDoc(docRef, newTask);

    setTasks((prev) => [...prev, { id: docRef.id, ...newTask }]);
    setNewTaskName("");
    setPriority("Medium");
  };

  const toggleTaskCompletion = async (taskId) => {
    const taskRef = doc(db, "tasks", taskId);
    const task = tasks.find((task) => task.id === taskId);

    if (!task) return;

    const updatedTask = { ...task, completed: !task.completed };
    await updateDoc(taskRef, { completed: updatedTask.completed });

    setTasks((prev) =>
      prev.map((task) => (task.id === taskId ? updatedTask : task))
    );
  };

  const deleteTask = async (taskId) => {
    const taskRef = doc(db, "tasks", taskId);
    await deleteDoc(taskRef);

    setTasks((prev) => prev.filter((task) => task.id !== taskId));
  };

  return (
    <Container>
      <h2>To-Do List</h2>
      <TaskList>
        {tasks.map((task) => (
          <Task key={task.id} completed={task.completed}>
            <TaskDetails>
              <TaskName completed={task.completed}>{task.name}</TaskName>
              <PriorityLabel>{task.priority}</PriorityLabel>
            </TaskDetails>
            <ButtonGroup>
              <Button
                completed={task.completed}
                onClick={() => toggleTaskCompletion(task.id)}
              >
                {task.completed ? "Undo" : "Complete"}
              </Button>
              <IconButton onClick={() => deleteTask(task.id)}>
                <FaTrash />
              </IconButton>
            </ButtonGroup>
          </Task>
        ))}
      </TaskList>
      <AddTaskForm onSubmit={addTask}>
        <TaskInput
          type="text"
          placeholder="Add a new task..."
          value={newTaskName}
          onChange={(e) => setNewTaskName(e.target.value)}
        />
        <SelectPriority
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </SelectPriority>
        <AddButton type="submit">Add Task</AddButton>
      </AddTaskForm>
    </Container>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100%;
  padding: 40px;
  background-color: #171c29;
  border-radius: 20px;
  font-family: "Comfortaa";

  h2 {
    font-weight: 300;
    text-align: center;
    margin-bottom: 20px;
  }
  @media (max-width: 1440px) {
    width: 47%;
  }
  @media (max-width: 1100px) {
    width: 100%;
  }
  @media (max-width: 500px) {
    padding: 40px 20px;
  }
`;

const TaskList = styled.div`
  margin-bottom: 20px;
  max-height: 310px;
  overflow-y: scroll;
`;

const Task = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1em;
  align-items: center;
  padding: 10px 15px;
  background: #20273a;
  border-radius: 15px;
  margin-bottom: 15px;
  animation: ${fadeIn} 0.3s ease;
  font-family: "Comfortaa";
`;

const TaskDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const TaskName = styled.span`
  font-size: 16px;
  color: ${(props) => (props.completed ? "#5a5a5a" : "#ffffff")};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
`;

const PriorityLabel = styled.span`
  font-size: 12px;
  color: #d6d6d6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  padding: 8px 10px;
  font-size: 14px;
  background: ${(props) => (props.completed ? "#d9544f37" : "#ffffff")};
  color: ${(props) => (props.completed ? "#ffffff" : "#303030")};
  border: none;
  border-radius: 10px;
  cursor: pointer;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
`;

const AddTaskForm = styled.form`
  display: flex;
  margin-top: 20px;
  font-family: "Comfortaa";
`;

const EditForm = styled.form`
  display: flex;
  margin-top: 20px;
  font-family: "Comfortaa";
`;

const TaskInput = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 14px;
  border-radius: 15px 0 0 15px;
  border: none;
  background-color: #3b445c;
  color: white;
  outline: none;
  font-family: "Comfortaa";
`;

const SelectPriority = styled.select`
  flex: 1;
  padding: 15px;
  font-size: 14px;
  background-color: #3b445c;
  color: white;
  border: none;
  outline: none;
  font-family: "Comfortaa";
`;

const AddButton = styled.button`
  padding: 10px 15px;
  font-size: 14px;
  background: #e7e7e7;
  color: #252525;
  border: none;
  border-radius: 0 15px 15px 0;
  cursor: pointer;
  font-family: "Comfortaa";
`;

const SaveButton = styled(AddButton)`
  background: #4caf50;
  color: white;
  font-family: "Comfortaa";
`;

export default ToDoList;
